import { Expense } from '../types';
import { getCriticalMissingFields, getSoftMissingFields } from './fieldLabels';

export type ReviewStatusDotState = 'red' | 'green' | 'pink' | 'grey' | 'none';

export type ReviewStatusFilter = ReviewStatusDotState | '';

export const ALL_REVIEW_STATUS_FILTER_STATES: ReviewStatusDotState[] = [
  'red',
  'green',
  'pink',
  'grey',
];

export function navItemMatchesStatusFilter(
  dotState: ReviewStatusDotState,
  selected: ReadonlySet<ReviewStatusDotState>
): boolean {
  if (
    selected.size === 0 ||
    selected.size === ALL_REVIEW_STATUS_FILTER_STATES.length
  ) {
    return true;
  }
  return selected.has(dotState);
}

export interface NavExpenseItem {
  id: string;
  creationTimestamp: string;
  dotState: ReviewStatusDotState;
}

export function expenseMatchesReviewStatusFilter(
  expense: Expense,
  reviewed: boolean,
  filter: ReviewStatusFilter
): boolean {
  if (!filter) return true;
  return getReviewStatusDotState(expense, reviewed) === filter;
}

export function expenseMatchesReviewStatusFilterSet(
  expense: Expense,
  reviewed: boolean,
  selected: ReadonlySet<ReviewStatusDotState>
): boolean {
  return navItemMatchesStatusFilter(getReviewStatusDotState(expense, reviewed), selected);
}

export function findNavNeighbors(
  sorted: NavExpenseItem[],
  activeId: string,
  activeTimestamp: string,
  selected: ReadonlySet<ReviewStatusDotState>
): { newer: NavExpenseItem | null; older: NavExpenseItem | null } {
  const matches = (item: NavExpenseItem) =>
    navItemMatchesStatusFilter(item.dotState, selected);
  const fullIdx = activeId ? sorted.findIndex((item) => item.id === activeId) : -1;

  let newer: NavExpenseItem | null = null;
  if (fullIdx >= 0) {
    for (let i = fullIdx - 1; i >= 0; i--) {
      if (matches(sorted[i])) {
        newer = sorted[i];
        break;
      }
    }
  } else if (activeTimestamp) {
    const currentTs = new Date(activeTimestamp).getTime();
    for (const item of sorted) {
      if (!matches(item)) continue;
      if (new Date(item.creationTimestamp || 0).getTime() > currentTs) {
        newer = item;
        break;
      }
    }
  }

  let older: NavExpenseItem | null = null;
  if (fullIdx >= 0) {
    for (let i = fullIdx + 1; i < sorted.length; i++) {
      if (matches(sorted[i])) {
        older = sorted[i];
        break;
      }
    }
  } else if (activeTimestamp) {
    const currentTs = new Date(activeTimestamp).getTime();
    for (const item of sorted) {
      if (!matches(item)) continue;
      if (new Date(item.creationTimestamp || 0).getTime() < currentTs) {
        older = item;
        break;
      }
    }
  }

  return { newer, older };
}

/** Closest matching expense by upload timestamp (newer first, then older). */
export function findNearestMatchingNavItem(
  sorted: NavExpenseItem[],
  activeId: string,
  activeTimestamp: string,
  selected: ReadonlySet<ReviewStatusDotState>
): NavExpenseItem | null {
  const matching = sorted.filter((item) =>
    navItemMatchesStatusFilter(item.dotState, selected)
  );
  if (matching.length === 0) return null;

  const current = matching.find((item) => item.id === activeId);
  if (current) return current;

  const fullIdx = activeId ? sorted.findIndex((item) => item.id === activeId) : -1;
  if (fullIdx >= 0) {
    for (let dist = 1; dist < sorted.length; dist++) {
      const newer = sorted[fullIdx - dist];
      const older = sorted[fullIdx + dist];
      if (newer && navItemMatchesStatusFilter(newer.dotState, selected)) return newer;
      if (older && navItemMatchesStatusFilter(older.dotState, selected)) return older;
    }
  }

  const currentTs = new Date(activeTimestamp || 0).getTime();
  let best = matching[0];
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const item of matching) {
    const diff = Math.abs(new Date(item.creationTimestamp || 0).getTime() - currentTs);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = item;
    }
  }
  return best;
}

/** 1-based position of the active expense among filtered items (newest = 1). */
export function findNavPosition(
  sorted: NavExpenseItem[],
  activeId: string,
  selected: ReadonlySet<ReviewStatusDotState>
): { current: number; total: number } {
  const matching = sorted.filter((item) =>
    navItemMatchesStatusFilter(item.dotState, selected)
  );
  const index = activeId ? matching.findIndex((item) => item.id === activeId) : -1;
  return {
    current: index >= 0 ? index + 1 : 0,
    total: matching.length,
  };
}

export function amountNeedsReviewFromLevel(level: unknown): boolean {
  if (level === null || level === undefined || level === '') return false;
  const value = Number(level);
  return Number.isFinite(value) && value === 0;
}

/** Same priority as the main table; grey = pending review with no missing fields. */
export function getReviewStatusDotState(
  expense: Expense,
  reviewed: boolean
): ReviewStatusDotState {
  if (getCriticalMissingFields(expense).length > 0) return 'red';
  if (expense.amountNeedsReview && !reviewed) return 'red';
  if (reviewed) return 'green';
  if (getSoftMissingFields(expense).length > 0) return 'pink'; // L2 missing
  return 'grey';
}

export function getReviewStatusDotTitle(state: ReviewStatusDotState, expense: Expense): string {
  switch (state) {
    case 'red': {
      const titles = getCriticalMissingFields(expense).map((m) => m.label).join(', ');
      if (titles) return `חסרים: ${titles}`;
      if (expense.amountNeedsReview) return 'יש לבדוק את הסכום';
      return 'חסרים';
    }
    case 'green':
      return 'נבדק ונשמר';
    case 'pink': {
      const titles = getSoftMissingFields(expense).map((m) => m.label).join(', ');
      return `חסרים: ${titles}`;
    }
    case 'grey':
      return 'טרם נבדק';
    default:
      return '';
  }
}
